/** @format */

import { DragOutlined, MenuFoldOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Empty, Input, Segmented, Tooltip } from "antd";
import React, { useMemo, useState } from "react";
import { TOrgUser } from "../../../../redux-modules/declarations/maps/organigramma";
import { DRAG_MIME } from "../context";
import { TOrgIndexEntry } from "../utils/layout";
import { searchHaystack, surnameFirstOf } from "../utils/people";
import { OrgAvatar } from "./OrgAvatar";

type TTab = "out" | "in";

interface Props {
  users: TOrgUser[];
  index: Map<number, TOrgIndexEntry>;
  canUpdate: boolean;
  focusedId: number | null;
  onClose: () => void;
  /** persona fuori dal grafico: apre la scheda per collocarla */
  onPlace: (userId: number) => void;
  /** persona nel grafico: la mostra */
  onLocate: (personId: number) => void;
  onDragStart: (userId: number) => void;
  onDragEnd: () => void;
}

interface TRow {
  id: number;
  user: TOrgUser;
  label: string;
  subtitle: string | null;
  tags: string[];
}

export const PeoplePanel = ({
  users,
  index,
  canUpdate,
  focusedId,
  onClose,
  onPlace,
  onLocate,
  onDragStart,
  onDragEnd,
}: Props): JSX.Element => {
  const [tab, setTab] = useState<TTab>("out");
  const [query, setQuery] = useState("");

  // Da collocare: persone attive non ancora nel grafico
  const outRows = useMemo<TRow[]>(
    () =>
      users
        .filter(u => !index.has(u.id) && !u.is_disabled)
        .map(u => ({
          id: u.id,
          user: u,
          label: surnameFirstOf(u),
          subtitle: null,
          tags: u.is_occasionale ? ["Occasionale"] : [],
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "it")),
    [users, index],
  );

  // Nel grafico: dati presi dall'albero (ruolo dell'organigramma)
  const inRows = useMemo<TRow[]>(() => {
    const byId = new Map(users.map(u => [u.id, u] as [number, TOrgUser]));
    const rows: TRow[] = [];
    index.forEach(({ person }) => {
      const user: TOrgUser = byId.get(person.id) || {
        id: person.id,
        name: person.name,
        surname: person.surname,
        avatar_storage_url: person.avatar_storage_url,
        has_avatar: person.has_avatar,
        is_disabled: person.is_disabled,
        is_occasionale: false,
        in_chart: true,
        role_id: person.role_id,
        role_name: person.role_name,
      };
      const tags: string[] = [];
      if (person.is_staff) tags.push("Staff");
      if (person.is_disabled) tags.push("Disabilitato");
      rows.push({ id: person.id, user, label: surnameFirstOf(person), subtitle: person.role_name, tags });
    });
    return rows.sort((a, b) => a.label.localeCompare(b.label, "it"));
  }, [users, index]);

  const rows = tab === "out" ? outRows : inRows;
  const q = query.trim().toLowerCase();
  const filtered = q
    ? rows.filter(r => searchHaystack({ ...r.user, role_name: r.subtitle }).includes(q))
    : rows;

  const draggable = canUpdate;

  return (
    <aside className="org-panel" aria-label="Persone">
      <div className="org-panel__head">
        <span className="org-panel__title">Persone</span>
        <Tooltip title="Chiudi il pannello">
          <Button type="text" size="small" icon={<MenuFoldOutlined />} onClick={onClose} />
        </Tooltip>
      </div>

      <div className="org-panel__controls">
        <Segmented
          block
          size="small"
          value={tab}
          onChange={(v): void => setTab(v as TTab)}
          options={[
            { label: `Da collocare (${outRows.length})`, value: "out" },
            { label: `Nel grafico (${inRows.length})`, value: "in" },
          ]}
        />
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.35)" }} />}
          placeholder="Cerca per nome o ruolo"
          value={query}
          onChange={(e): void => setQuery(e.target.value)}
        />
        {draggable && filtered.length > 0 && (
          <div className="org-panel__hint">
            <DragOutlined />
            <span>
              {tab === "out"
                ? "Trascina una persona su un responsabile per aggiungerla al suo team, oppure cliccala."
                : "Trascina una persona su un altro responsabile per spostarla, oppure cliccala per trovarla."}
            </span>
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="org-panel__empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              q
                ? "Nessuna persona corrisponde alla ricerca."
                : tab === "out"
                ? "Tutte le persone attive sono nell'organigramma."
                : "L'organigramma è ancora vuoto."
            }
          />
        </div>
      ) : (
        <ul className="org-panel__list">
          {filtered.map(row => (
            <li key={row.id}>
              <button
                type="button"
                className={`org-panel__item${focusedId === row.id ? " is-active" : ""}`}
                draggable={draggable}
                onDragStart={(e): void => {
                  e.dataTransfer.setData(DRAG_MIME, String(row.id));
                  e.dataTransfer.setData("text/plain", row.label);
                  e.dataTransfer.effectAllowed = "copyMove";
                  onDragStart(row.id);
                }}
                onDragEnd={onDragEnd}
                onClick={(): void => (tab === "out" ? onPlace(row.id) : onLocate(row.id))}
                title={tab === "out" ? "Colloca nell'organigramma" : "Mostra nell'organigramma"}
              >
                <OrgAvatar person={row.user} size={32} />
                <span className="org-panel__text">
                  <span className="org-panel__name">{row.label}</span>
                  {row.subtitle && <span className="org-panel__role">{row.subtitle}</span>}
                </span>
                {row.tags.map(tag => (
                  <span key={tag} className={`org-pill${tag === "Staff" ? " org-pill--staff" : ""}`}>
                    {tag}
                  </span>
                ))}
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
};
